import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { isOn, useFeatures } from '@/lib/features';
import { reconcileSignedInUser, type ServerUser } from '@/lib/session';
import { onRefusal } from '@/lib/refusals';

/**
 * READS THE SIGNED-IN ROLE AGAIN (switch SESSION_REFRESH, "Refresh sign-in").
 *
 * The role used to come from the sign-in response and nowhere else, so a demotion — say,
 * an HR user made an Interviewer — showed only after they signed out and in again; until
 * then the menu and pages were the old role's and the server refused them one by one.
 * With the switch on, GET /auth/me is read:
 *  - when the app opens (this mounts with the signed-in layout);
 *  - when the window regains focus after being left (a blur or a hidden tab first, so the
 *    paired focus and visibility events of one return cost one request, not two);
 *  - after a refusal from the server (lib/refusals.ts), at most once in REFUSAL_GAP_MS, so a
 *    page whose requests are all refused costs one re-read, not one each.
 * What it answers replaces what is stored (lib/session.ts reconcileSignedInUser), which
 * re-routes at once: the role gate in App.tsx sends a new Interviewer to My interviews.
 *
 * With the switch off it asks for nothing, as today.
 *
 * Renders nothing. /auth/me is on the server's interviewer allow-list, so this works for
 * every role.
 */

const REFUSAL_GAP_MS = 2_000;

export const ME_KEY = ['auth', 'me'] as const;

export function SessionKeeper() {
  const features = useFeatures();
  const on = isOn(features, 'SESSION_REFRESH');
  const userId = useAuthStore((s) => s.user?.id);

  const { data, refetch } = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => (await api.get('/auth/me')).data.data as ServerUser,
    enabled: on && !!userId,
    // Always asked afresh: the point is to notice a change, so a cached answer is no answer.
    staleTime: 0,
    gcTime: 0,
    // Focus is handled below; TanStack's own focus refetch would double it.
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (data) reconcileSignedInUser(data);
  }, [data]);

  // A ref, so the listeners below see the current refetch without re-subscribing.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!on || !userId) return;
    let away = false;
    const leave = () => { away = true; };
    const back = () => {
      if (!away) return;
      away = false;
      void refetchRef.current();
    };
    const onVisibility = () => (document.visibilityState === 'hidden' ? leave() : back());
    window.addEventListener('blur', leave);
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', onVisibility);

    let lastRefusalAt = 0;
    const stopRefusals = onRefusal(() => {
      const now = Date.now();
      if (now - lastRefusalAt < REFUSAL_GAP_MS) return;
      lastRefusalAt = now;
      void refetchRef.current();
    });

    return () => {
      window.removeEventListener('blur', leave);
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', onVisibility);
      stopRefusals();
    };
  }, [on, userId]);

  return null;
}
